/**
 * LinkedAccountCard Component
 * Displays a linked bank account with status, sync, and unlink actions
 */

import { useState } from 'react';
import type { LinkedBankAccount, SyncResult } from '../api/linked-bank-accounts.api';
import { unlinkBankAccount, syncBankAccount } from '../api/linked-bank-accounts.api';
import { formatDateTime } from '../utils/date';

interface LinkedAccountCardProps {
  account: LinkedBankAccount;
  onUnlinked: () => void;
  onSynced: () => void;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-success/10 text-success',
  disconnected: 'bg-warning/10 text-warning',
  error: 'bg-destructive/10 text-destructive',
};

export function LinkedAccountCard({ account, onUnlinked, onSynced }: LinkedAccountCardProps): JSX.Element {
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const handleUnlink = async () => {
    try {
      setIsUnlinking(true);
      setError(null);
      await unlinkBankAccount(account.id);
      onUnlinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink account');
    } finally {
      setIsUnlinking(false);
      setShowConfirm(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);
      setSyncResult(null);
      const result = await syncBankAccount(account.id);
      setSyncResult(result);
      onSynced();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync account');
    } finally {
      setIsSyncing(false);
    }
  };

  const statusClass = STATUS_STYLES[account.status] ?? 'bg-muted text-foreground';

  return (
    <div
      data-testid={`linked-account-card-${account.id}`}
      className="bg-card rounded-lg p-4 border border-border"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{account.institutionName}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {account.accountName} ••••{account.accountMask}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 capitalize">{account.accountType}</p>
        </div>
        <span
          data-testid={`account-status-badge-${account.id}`}
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${statusClass}`}
        >
          {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
        </span>
      </div>

      {account.lastSyncedAt && (
        <p className="text-xs text-muted-foreground mb-3">
          Last synced: {formatDateTime(account.lastSyncedAt)}
        </p>
      )}

      {error && (
        <p className="text-xs text-destructive mb-3">{error}</p>
      )}

      {syncResult && (
        <p
          data-testid={`sync-result-${account.id}`}
          className="text-xs text-muted-foreground mb-3"
        >
          {syncResult.added === 0 && syncResult.modified === 0 && syncResult.removed === 0
            ? 'No new transactions'
            : [
                syncResult.added > 0 ? `${syncResult.added} added` : null,
                syncResult.modified > 0 ? `${syncResult.modified} updated` : null,
                syncResult.removed > 0 ? `${syncResult.removed} removed` : null,
              ]
                .filter(Boolean)
                .join(', ')}
        </p>
      )}

      {showConfirm ? (
        <div className="flex gap-2 mt-2">
          <p className="text-xs text-muted-foreground flex-1">Remove this account?</p>
          <button
            type="button"
            onClick={handleUnlink}
            disabled={isUnlinking}
            className="px-3 py-1.5 text-xs font-medium text-destructive-foreground bg-destructive rounded hover:bg-destructive/90 disabled:opacity-50 min-h-[36px]"
          >
            {isUnlinking ? 'Removing...' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="px-3 py-1.5 text-xs font-medium text-foreground border border-border rounded hover:bg-accent min-h-[36px]"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            data-testid={`sync-account-button-${account.id}`}
            onClick={handleSync}
            disabled={isSyncing}
            className="px-3 py-1.5 text-xs font-medium text-primary border border-primary/20 rounded hover:bg-primary/5 disabled:opacity-50 min-h-[36px]"
          >
            {isSyncing ? 'Syncing...' : 'Sync'}
          </button>
          <button
            type="button"
            data-testid={`unlink-account-button-${account.id}`}
            onClick={() => setShowConfirm(true)}
            className="px-3 py-1.5 text-xs font-medium text-destructive border border-destructive/20 rounded hover:bg-destructive/5 min-h-[36px]"
          >
            Unlink
          </button>
        </div>
      )}
    </div>
  );
}
